import { Bloc } from "../../../../components/organisms/Bloc"
import { loc } from "../../../../core/utils"
import { PackServiceContext } from "../../../../digital-journey/ServicePage/context/PackServiceContext"
import Select from "../../../../form/Select"
import { getUserToken, getUsername } from "../../../../service/authorization"
import useIsMultiProducts from "../../hooks/useIsMultiProducts"
import useVerifyPromotionCode from "../../hooks/useVerifyPromotionCode"
import AddProductButton from "../AddProductButton/AddProductButton"
import ProductCreationModal from "../ProductCreationModal"
import { ProductLine } from "../ProductLine"
import PromotionCodeVerifier from "../PromotionCodeVerifier/PromotionCodeVerifier"
import "./simulation-input.scss"
import useSimulationInput from "./useSimulationInput"
import React, { useContext, useMemo } from "react"
import { Button, Col, Row, Spinner } from "react-bootstrap"
import { useField } from "react-final-form"
import { FieldArray } from "react-final-form-arrays"

const maxNumberOfProductLine = 4

function PromotionCodeMessageValidation({ promoCode, isValid }) {
  switch (isValid) {
    case true:
      return (
        <span>
          {loc("simulation_promotion_response_valid", {
            promotionCode: promoCode
          })}
        </span>
      )
    case false:
      return <span>{loc("simulation_promotion_response_invalid")}</span>
    default:
      return null
  }
}

const SimulationInput = ({ tunnelData, handleSubmit, setProduct, values, form, push, hasValidationErrors }) => {
  const { apiUrl, lang, opl } = tunnelData

  const token = useMemo(() => getUserToken(), [])
  const username = useMemo(() => getUsername(), [])

  const supplierId = values?.shop?.value?.split("-")[0]
  const isMultiProduct = useIsMultiProducts(token, supplierId, apiUrl)
  const { verifyiedPromotionCode, storeVerifyiedPromotionCode } = useContext(PackServiceContext)

  const { resetPackContext, setSelectedPack, isLoadingOrder, isLoadingDeliveryMethods } = useSimulationInput(opl)

  const { input } = useField("promoCode")

  const { isVerifyingPromoCode } = useVerifyPromotionCode(supplierId, values, handleSubmit)

  const verifyPromotionCode = () => {
    storeVerifyiedPromotionCode(input.value)
  }

  if (isLoadingOrder || isLoadingDeliveryMethods)
    return (
      <Row>
        <span className="spinner-box">
          <Spinner animation="border" className="spinner" />
        </span>
      </Row>
    )

  return (
    <>
      <Row className="simulation-row">
        <Col lg={6}>
          <Bloc title={loc("simulation_shop_block_title")}>
            <Select
              name="shop"
              label={loc("simulation_shop_input_label")}
              url={`${apiUrl}middle-business-service/api/v2/user/suppliers?username=${username}`}
              token={token}
              extract={(items) =>
                items.map((item) => {
                  return {
                    value: `${item.id}-${item.supplierReference}`,
                    label: item.name
                  }
                })
              }
              oneElement={true}
            />
          </Bloc>
        </Col>
        <Col lg={6}>
          <Bloc title={loc("simulation_delivery_block_title")}>
            <Select
              name="delivery"
              label={loc("simulation_delivery_input_label")}
              url={
                values.shop &&
                `${apiUrl}middle-business-service/api/v1/parameters/find-delivery-methods-by-country-code/${
                  values.shop.value.split("-")[0]
                }?countryCode=${lang.split("-")[1]}`
              }
              token={token}
              extract={(items) =>
                items.map((item) => {
                  return { value: item.id, label: loc(item.label) }
                })
              }
              disabled={!values.shop}
              oneElement={true}
            />
          </Bloc>
        </Col>
      </Row>
      <Row className="simulation-row">
        <Col lg={12}>
          <Bloc title={loc("simulation_products_block_title")}>
            <FieldArray name="products">
              {({ fields }) => (
                <div className="product-line-elements">
                  {fields.map((name, index) => {
                    return (
                      <div key={name}>
                        <ProductLine
                          key={name + index}
                          apiUrl={apiUrl}
                          token={token}
                          tunnelData={tunnelData}
                          shopValues={values}
                          setProduct={setProduct}
                          name={name}
                          index={index}
                          isMultiProduct={isMultiProduct}
                          deleteButton={
                            fields.length > 1 && (
                              <span
                                className="cursor-pointer"
                                onClick={() => {
                                  fields.remove(index)
                                  setProduct(values)
                                }}
                              >
                                <Button variant="light">
                                  <i className="remove-product-button fas fa-trash-alt"></i> {loc("remove_product")}
                                </Button>
                              </span>
                            )
                          }
                          addProductModal={
                            <ProductCreationModal
                              tunnelData={tunnelData}
                              formValues={values}
                              apiUrl={apiUrl}
                              token={token}
                              updateField={(values) => {
                                fields.update(index, values)
                              }}
                            />
                          }
                        />
                      </div>
                    )
                  })}
                  {fields.length < maxNumberOfProductLine && (
                    <Row>
                      <div className="buttons">
                        <AddProductButton
                          isMultiProduct={isMultiProduct}
                          handleAddProduct={() => push("products", { quantity: "1" })}
                        />
                      </div>
                    </Row>
                  )}
                </div>
              )}
            </FieldArray>
          </Bloc>
        </Col>
      </Row>
      <Row className="simulation-row">
        <Col lg={12}>
          <Bloc title={loc("simulation_promotion_block_title")}>
            {isVerifyingPromoCode ? (
              <Row>
                <span className="spinner-box">
                  <Spinner animation="border" className="spinner" />
                </span>
              </Row>
            ) : (
              <div className="d-flex flex-column" style={{ gap: "2rem " }}>
                <PromotionCodeVerifier verifyPromotionCode={verifyPromotionCode} />
                <PromotionCodeMessageValidation
                  promoCode={verifyiedPromotionCode?.name}
                  isValid={verifyiedPromotionCode?.valid}
                />
              </div>
            )}
          </Bloc>
        </Col>
      </Row>
      <Row className="simulation-row">
        <Col lg={2}>
          <Button
            type="reset"
            variant="success"
            className="submit"
            onClick={() => {
              form.setConfig("keepDirtyOnReinitialize", false)
              form.reset()
              form.setConfig("keepDirtyOnReinitialize", true)
              resetPackContext({ isRetail: true })
              setSelectedPack(null)
              setProduct(null)
            }}
            disabled={
              !(values?.sku || values?.brand || values?.category?.label || values?.shop?.label || values?.family?.label)
            }
          >
            {loc("simulation_button_reset")}
          </Button>
        </Col>
        <Col lg={2}>
          <Button
            variant="success"
            className="submit simulation-btn-success"
            onClick={() => handleSubmit(values)}
            disabled={hasValidationErrors}
          >
            <i className="fas fa-rotate-90"></i>
            {loc("simulation_button_update")}
          </Button>
        </Col>
      </Row>
    </>
  )
}

export default React.memo(SimulationInput)
